/**
 * pd-data-client.v1.js — PageDuo client-side data transport (PDDataClient), MAJOR v1.
 *
 * MAJOR-pinned first-party asset (DATA_CLIENT_FILE in pd-reactive-runtime.registry.ts):
 * published v1 pages bake `/static/pd-data-client.v1.js`, so a breaking change here can
 * never reach an already-baked page — evolve v1 additively; a breaking change ships a
 * pd-data-client.v2.js alongside the v2 runtime.
 *
 * The one CLIENT module that talks to the server for DATASET DATA OBJECTS: fetch
 * them, save them — resiliently. Retry, jittered backoff, timeouts, a durable
 * write queue, idempotency, error classification, safe (de)serialization.
 *
 * Sibling to pd-reactive-runtime.js and built with the same discipline: VANILLA,
 * NO eval, no build step, ZERO dependencies (smallest possible surface on public
 * pages + trivially auditable). The reactive runtime owns binding + WHEN to fetch;
 * this module owns HOW to fetch/save robustly. It is standalone — usable without
 * the reactive runtime (e.g. by legacy hydrators). See specs/spec-data-client.md.
 *
 * THREE data transports (spec §1, §5.1):
 *   1. PageDuo backend — GET /ds/:readKey (read) + POST /ds/:writeKey (durable write).
 *      Both are OPAQUE capability keys; Bearer-auth when a token is supplied. The
 *      client is GENERIC: it knows nothing about what a key means (a form, a quiz, an
 *      upsert, a report) — just "query id + data object". No page/form/submission concept.
 *   2. External DIRECT — browser → third-party URL, no PageDuo hop. https-only,
 *      NO credentials/secrets, no userinfo/loopback hosts, CORS-bounded.
 *   3. External PROXY — GET /live-data/proxy?url=… (server-side CORS/keys/SSRF/cache).
 *
 * Runs identically in editor / preview / published via injected config; no eval
 * (preview strict CSP), no localStorage dependency (opaque-origin iframes), no
 * Service Worker. Retry policy mirrors ky/p-retry (Retry-After + full jitter +
 * retry-only-safe-status); hand-rolled to keep the surface tiny + reviewable.
 * Hardened per adversarial review 2026-07-12 (see spec §9).
 */
(function (global) {
  'use strict';
  var VERSION = '0.5.0';
  // Self-derive the API origin from THIS script's OWN served location, so a published
  // page needs NOTHING baked for data URLs — it just loads the script (served from the
  // API's /static/) and the client already knows the origin. `document.currentScript`
  // is only valid during the initial synchronous run, so capture it here at load time.
  var SELF_ORIGIN = '';
  try { var _cs = global.document && global.document.currentScript; if (_cs && _cs.src) SELF_ORIGIN = new URL(_cs.src, global.location && global.location.href).origin; } catch (e) {}

  // ── CLASS-LINK RESPONDENT ("Give it to my class", spec-return-loops-course-and-assign.md §3.4) ──
  // A student who tapped their name on the class link lands here with `#pd-s=<token>` in the URL
  // FRAGMENT — a fragment is never sent to any server, never in access logs, never in a Referer.
  // Read it ONCE, at load, keep it for this page in sessionStorage (this tab only), and take it out
  // of the address bar at once so a copied link or a later script never sees it. Each write then
  // carries it as `respondent`; the server ignores it unless it belongs to an open assignment of
  // THIS page. Additive: a page opened any other way sends nothing extra.
  // The NAME the student tapped travels beside the token (`&pd-n=`), so the page can fill its own
  // Name field instead of asking again (`PDDataClient.respondentName()`, read by the runtime). It is
  // for display only: this client never sends it anywhere (the server names the row from the token).
  // It is accepted only together with a token, kept and cleared the same way.
  var RESPONDENT = '', RESPONDENT_NAME = '';
  try {
    var _loc = global.location, _path = (_loc && _loc.pathname) || '', _skey = 'pd-s:' + _path, _nkey = 'pd-n:' + _path;
    var _m = _loc && _loc.hash ? /(?:^#|&)pd-s=([A-Za-z0-9_-]{24,64})(?:&|$)/.exec(_loc.hash) : null;
    if (_m) {
      RESPONDENT = _m[1];
      var _n = /(?:^#|&)pd-n=([^&]*)/.exec(_loc.hash);
      if (_n) { try { RESPONDENT_NAME = decodeURIComponent(_n[1]).replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 120); } catch (e) { RESPONDENT_NAME = ''; } }
      try { global.sessionStorage && global.sessionStorage.setItem(_skey, RESPONDENT); } catch (e) {}
      try { if (global.sessionStorage) { if (RESPONDENT_NAME) global.sessionStorage.setItem(_nkey, RESPONDENT_NAME); else if (global.sessionStorage.removeItem) global.sessionStorage.removeItem(_nkey); } } catch (e) {}
      try {
        var _rest = _loc.hash.replace(/(^#|&)pd-s=[A-Za-z0-9_-]+/, '$1').replace(/(^#|&)pd-n=[^&]*/, '$1').replace(/&&+/g, '&').replace(/^#&/, '#').replace(/&$/, '');
        global.history && global.history.replaceState && global.history.replaceState(global.history.state, '', _loc.pathname + _loc.search + (_rest === '#' ? '' : _rest));
      } catch (e) {}
    } else {
      try { RESPONDENT = (global.sessionStorage && global.sessionStorage.getItem(_skey)) || ''; } catch (e) {}
      try { RESPONDENT_NAME = (RESPONDENT && global.sessionStorage && global.sessionStorage.getItem(_nkey)) || ''; } catch (e) {}
    }
  } catch (e) { RESPONDENT = ''; RESPONDENT_NAME = ''; }

  // A random id for THIS browser on THIS site (localStorage is per origin, so it never spans two
  // authors' sites). Sent with each write ONLY so a page set to "one answer per device" can hold
  // to it; the server hashes it and keeps it in memory for 24 h, never in the database. No storage
  // (a private window, an opaque iframe) → no id, and the page simply is not limited.
  var DEVICE = '';
  try {
    var _ls = global.localStorage;
    if (_ls) {
      DEVICE = _ls.getItem('pd_dev_v1') || '';
      if (!/^[a-f0-9]{16,64}$/.test(DEVICE) && global.crypto && global.crypto.getRandomValues) {
        var _b = new Uint8Array(12); global.crypto.getRandomValues(_b);
        DEVICE = Array.prototype.map.call(_b, function (x) { return (x + 256).toString(16).slice(1); }).join('');
        _ls.setItem('pd_dev_v1', DEVICE);
      }
    }
  } catch (e) { DEVICE = ''; }

  // ── typed errors: consumers branch on .type; .retryable drives the retry loop ──
  function DataError(type, message, extra) {
    var e = new Error(message || type);
    e.name = 'PDDataError'; e.type = type; e.retryable = false;
    if (extra) { for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) e[k] = extra[k]; }
    return e;
  }
  // Normalize ANY thrown value to a typed PDDataError so read()/save() never reject
  // with a raw TypeError (contract: every rejection is a PDDataError).
  function asDataError(err, fallbackType) {
    if (err && err.name === 'PDDataError') return err;
    return DataError(fallbackType || 'network', (err && err.message) || String(err), { retryable: fallbackType !== 'parse', cause: err });
  }
  var RETRY_STATUS = { 408: 1, 425: 1, 429: 1, 500: 1, 502: 1, 503: 1, 504: 1 };

  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  // Full-jitter exponential backoff (AWS "Exponential Backoff And Jitter").
  function backoffMs(attempt, base, cap) { return Math.random() * Math.min(cap, base * Math.pow(2, attempt - 1)); }
  // Honor Retry-After (seconds OR HTTP-date). CAPPED to `cap` — a remote server
  // (attacker-controlled on a direct external read) must never be able to park a
  // request for hours/years or overflow setTimeout's 32-bit delay (→ retry storm).
  function retryAfterMs(res, cap) {
    try {
      var ra = res && res.headers && res.headers.get && res.headers.get('Retry-After');
      if (!ra) return null;
      var ms = /^\d+$/.test(ra) ? parseInt(ra, 10) * 1000 : (function () { var t = Date.parse(ra); return isNaN(t) ? null : Math.max(0, t - Date.now()); })();
      return ms == null ? null : Math.min(ms, cap);
    } catch (e) { return null; }
  }

  // ── SAFE deserialization (data stays DATA on the way IN, too) ──
  // JSON.parse (never eval). A reviver DROPS __proto__ / constructor / prototype
  // (the JSON prototype-pollution vectors — matches secure-json-parse + spec §8c;
  // dataset responses are attacker-writable (any visitor holding a writeKey), and this module
  // is a standalone boundary a naive downstream merge could exploit). Then a
  // bounded, ITERATIVE walk caps depth + node count so a pathological payload can't
  // blow the stack or DoS the model. Byte-cap (below) is the primary guard.
  var POLLUTE = { __proto__: 1, constructor: 1, prototype: 1 };
  function safeParse(text, maxDepth, maxNodes) {
    var data = JSON.parse(text, function (key, value) { return POLLUTE[key] ? undefined : value; });
    var stack = [[data, 1]], nodes = 0;
    while (stack.length) {
      var top = stack.pop(), v = top[0], depth = top[1];
      if (v && typeof v === 'object') {
        if (depth > maxDepth) throw DataError('parse', 'response nesting exceeds maxDepth ' + maxDepth, { retryable: false });
        for (var k in v) if (Object.prototype.hasOwnProperty.call(v, k)) {
          if (++nodes > maxNodes) throw DataError('parse', 'response node count exceeds cap', { retryable: false });
          var child = v[k]; if (child && typeof child === 'object') stack.push([child, depth + 1]);
        }
      }
    }
    return data;
  }
  function byteLen(s) { try { if (global.Blob) return new global.Blob([s]).size; if (global.TextEncoder) return new global.TextEncoder().encode(s).length; } catch (e) {} return s.length; }
  // Cryptographically-strong idempotency key when available (project security pref).
  function genKey() {
    try {
      if (global.crypto && global.crypto.getRandomValues) {
        var a = new Uint8Array(9); global.crypto.getRandomValues(a);
        return 'w_' + Date.now().toString(36) + '_' + Array.prototype.map.call(a, function (b) { return (b + 256).toString(16).slice(1); }).join('');
      }
    } catch (e) {}
    return 'w_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 11);
  }

  function boot(opts) {
    opts = opts || {};
    // The PageDuo API origin is NEVER hard-coded — it RESOLVES, so a published page
    // needs zero per-page publish transformation for data URLs. Resolution order:
    //   1. explicit opts.baseUrl               (editor/preview inject it)
    //   2. window.__clawdriveApiBase / <meta>  (optional server-baked override)
    //   3. SELF_ORIGIN                          (the client's own script URL — the
    //      published page just includes the script; the client self-derives the origin)
    //   4. relative ''                          (same-origin fallback)
    var baseUrl = (function () {
      var b = opts.baseUrl;
      if (b == null) {
        try {
          b = global.__clawdriveApiBase;
          if (b == null && global.document && document.querySelector) { var mt = document.querySelector('meta[name="pageduo:api-base"]'); b = mt && mt.getAttribute('content'); }
        } catch (e) {}
        if (b == null && SELF_ORIGIN) b = SELF_ORIGIN;
      }
      return String(b || '').replace(/\/+$/, '');
    })();
    var authFn = typeof opts.auth === 'function' ? opts.auth : function () { return null; };
    var mode = opts.mode || 'published';
    var apiFetch = opts.fetch || function (u, i) { return global.fetch(u, i); };
    var credsMode = opts.credentials || 'same-origin';
    var MAX_READ = opts.maxReadAttempts > 0 ? opts.maxReadAttempts : 4;
    var MAX_WRITE = opts.maxWriteAttempts > 0 ? opts.maxWriteAttempts : 12;
    var READ_TO = opts.readTimeoutMs > 0 ? opts.readTimeoutMs : 15000;
    var WRITE_TO = opts.writeTimeoutMs > 0 ? opts.writeTimeoutMs : 20000;
    var BASE_DELAY = opts.baseDelayMs > 0 ? opts.baseDelayMs : 1000;
    var MAX_DELAY = opts.maxDelayMs > 0 ? opts.maxDelayMs : 30000;
    var MAX_BYTES = opts.maxBytes > 0 ? opts.maxBytes : 5 * 1024 * 1024;
    var MAX_DEPTH = opts.maxDepth > 0 ? opts.maxDepth : 64;
    var MAX_NODES = opts.maxNodes > 0 ? opts.maxNodes : 500000;
    var MAX_QUEUE = opts.maxQueue > 0 ? opts.maxQueue : 500;   // cap recovered/live queue
    var MAX_STATUS = opts.maxStatus > 0 ? opts.maxStatus : 2000;
    var destroyed = false;
    var liveACs = [];                                          // in-flight AbortControllers (aborted on destroy)

    function log(op, e) { try { if (global.console && global.console.warn) global.console.warn('[pd-data-client] ' + op + ':', (e && e.message) || e); } catch (_) {} }

    // ── descriptor → { url, method, credentialed, direct } (spec §4: "just a URL") ──
    function datasetParams(params) {
      if (!params) return ''; var q = [];
      for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) {
        var v = params[k]; if (v == null || v === '') continue;
        (Array.isArray(v) ? v : [v]).forEach(function (x) { q.push('p.' + encodeURIComponent(k) + '=' + encodeURIComponent(x)); });
      }
      return q.join('&');
    }
    function withQuery(url, params) {
      if (!params) return url; var q = [];
      for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) {
        var v = params[k]; if (v == null) continue;
        (Array.isArray(v) ? v : [v]).forEach(function (x) { q.push(encodeURIComponent(k) + '=' + encodeURIComponent(x)); });
      }
      return q.length ? url + (url.indexOf('?') >= 0 ? '&' : '?') + q.join('&') : url;
    }
    // Reject obvious LAN/loopback/link-local hosts for DIRECT reads. CORS blocks
    // reading the response, but the GET side-effect fires — so keep an author-driven
    // published page from probing the visitor's own intranet. (Not a proxy-for-others.)
    function isPrivateHost(h) {
      h = String(h || '').toLowerCase();
      return h === 'localhost' || h === '0.0.0.0' || /(^|\.)local$/.test(h) ||
        /^127\./.test(h) || /^10\./.test(h) || /^192\.168\./.test(h) || /^169\.254\./.test(h) ||
        /^172\.(1[6-9]|2\d|3[01])\./.test(h) || h === '[::1]' || h.indexOf('[fc') === 0 || h.indexOf('[fd') === 0 || h.indexOf('[fe80') === 0;
    }
    function resolveRead(d) {
      if (!d || typeof d !== 'object') throw DataError('config', 'read needs a descriptor', { retryable: false });
      if (d.kind === 'dataset') {
        if (!d.readKey) throw DataError('config', 'dataset read needs readKey', { retryable: false });
        var qs = datasetParams(d.params);
        return { url: baseUrl + '/api/v1/ds/' + encodeURIComponent(d.readKey) + (qs ? '?' + qs : ''), method: 'GET', credentialed: true };
      }
      if (d.kind === 'external') {
        var t = d.transport || 'proxy';
        if (t === 'direct') {
          var target = withQuery(d.url, d.params), u;
          try { u = new URL(target); } catch (e) { throw DataError('config', 'invalid external url', { retryable: false }); }
          if (u.protocol !== 'https:') throw DataError('config', 'direct external read must be https (got ' + u.protocol + ')', { retryable: false });
          if (u.username || u.password) throw DataError('config', 'direct external url must not carry userinfo/credentials', { retryable: false });
          if (isPrivateHost(u.hostname)) throw DataError('config', 'direct external read blocked for private/loopback host', { retryable: false });
          return { url: u.href, method: 'GET', credentialed: false, direct: true };
        }
        if (!d.url) throw DataError('config', 'external proxy read needs url', { retryable: false });
        return { url: baseUrl + '/api/v1/live-data/proxy?url=' + encodeURIComponent(withQuery(d.url, d.params)), method: 'GET', credentialed: true };
      }
      throw DataError('config', 'unknown read kind "' + d.kind + '"', { retryable: false });
    }

    // ── one HTTP attempt with a hard timeout + error classification ──
    function fetchOnce(resolved, timeout, body, idem) {
      var ac = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timedOut = false, to = null, settled = false;
      if (ac) { liveACs.push(ac); to = setTimeout(function () { timedOut = true; try { ac.abort(); } catch (e) {} }, timeout); }
      function cleanup() { if (settled) return; settled = true; if (to) clearTimeout(to); if (ac) { var i = liveACs.indexOf(ac); if (i >= 0) liveACs.splice(i, 1); } }
      var headers = {};
      if (resolved.credentialed) { var tk = authFn(); if (tk) headers['Authorization'] = 'Bearer ' + tk; }
      var init = { method: resolved.method, headers: headers, credentials: resolved.direct ? 'omit' : credsMode };
      if (ac) init.signal = ac.signal;
      if (body != null) { headers['Content-Type'] = 'application/json'; init.body = body; }
      if (idem) headers['Idempotency-Key'] = idem;
      var p = apiFetch(resolved.url, init);
      // No-AbortController fallback: a race timeout still bounds the wait (can't
      // cancel the socket, but the caller won't hang forever).
      if (!ac) p = Promise.race([p, sleep(timeout).then(function () { timedOut = true; throw DataError('timeout', 'request timed out'); })]);
      return p.then(function (res) { cleanup(); return res; }, function (err) {
        cleanup();
        if (err && err.name === 'PDDataError') throw err;
        throw DataError(timedOut ? 'timeout' : 'network', timedOut ? 'request timed out after ' + timeout + 'ms' : ('network/CORS error' + (err && err.message ? ': ' + err.message : '')), { retryable: true, direct: !!resolved.direct, cause: err });
      });
    }
    function readCappedJSON(res) {
      var cl = res.headers && res.headers.get && res.headers.get('Content-Length');
      if (cl && parseInt(cl, 10) > MAX_BYTES) return Promise.reject(DataError('size', 'response exceeds maxBytes', { retryable: false }));
      return res.text().then(function (text) {
        if (text == null || /^\s*$/.test(text)) return null;                 // empty/204 success → null (not a parse error)
        if (byteLen(text) > MAX_BYTES) throw DataError('size', 'response exceeds maxBytes', { retryable: false });
        try { return safeParse(text, MAX_DEPTH, MAX_NODES); }
        catch (e) { if (e.name === 'PDDataError') throw e; throw DataError('parse', 'invalid JSON in response', { retryable: false, cause: e }); }
      }, function (e) { throw asDataError(e, 'parse'); });                     // text() decode error → typed
    }
    function attemptRead(resolved, attempt) {
      return fetchOnce(resolved, READ_TO).then(function (res) {
        if (res.ok) return readCappedJSON(res);
        if (RETRY_STATUS[res.status] && attempt < MAX_READ && !destroyed) {
          var d = retryAfterMs(res, MAX_DELAY); d = (d != null) ? d : backoffMs(attempt, BASE_DELAY, MAX_DELAY);
          return sleep(d).then(function () { if (destroyed) throw DataError('destroyed', 'client destroyed', { retryable: false }); return attemptRead(resolved, attempt + 1); });
        }
        return res.text().then(function (t) { throw DataError('http', 'HTTP ' + res.status, { status: res.status, retryable: false, bodySample: String(t).slice(0, 200) }); },
          function () { throw DataError('http', 'HTTP ' + res.status, { status: res.status, retryable: false }); });
      }, function (err) {
        err = asDataError(err);
        if (err.retryable && attempt < MAX_READ && !destroyed) return sleep(backoffMs(attempt, BASE_DELAY, MAX_DELAY)).then(function () { if (destroyed) throw DataError('destroyed', 'client destroyed', { retryable: false }); return attemptRead(resolved, attempt + 1); });
        throw err;
      });
    }

    // ── READ: coalesced (dedupe concurrent identical reads), direct→proxy fallback ──
    var inflight = Object.create(null);
    function read(descriptor) {
      if (destroyed) return Promise.reject(DataError('destroyed', 'client destroyed', { retryable: false }));
      var resolved;
      try { resolved = resolveRead(descriptor); } catch (e) { return Promise.reject(e); }
      var key = resolved.method + ' ' + resolved.url;
      if (inflight[key]) return inflight[key];
      var p = attemptRead(resolved, 1).catch(function (err) {
        if (descriptor.kind === 'external' && (descriptor.transport || 'proxy') === 'direct' && descriptor.fallbackToProxy &&
            (err.type === 'network' || err.type === 'timeout')) {
          log('direct-read fell back to proxy', err);
          var viaProxy; try { viaProxy = resolveRead({ kind: 'external', url: descriptor.url, params: descriptor.params, transport: 'proxy' }); } catch (e2) { throw err; }
          return attemptRead(viaProxy, 1);
        }
        throw err;
      });
      var settle = function () { delete inflight[key]; };
      p.then(settle, settle);            // always clear the coalescing entry (capped Retry-After ⇒ it always settles)
      inflight[key] = p;
      return p;
    }
    // (Pagination is NOT a method — it's just params on a read: the query declares a
    //  cursor/limit param and the client passes it in `params`, like any other.)

    // ── WRITE queue: durable, AT-LEAST-ONCE with IDEMPOTENT reconciliation ──
    // The POST fires from the in-memory queue (localStorage is a best-effort MIRROR),
    // so a save is never lost when storage is blocked (private mode / opaque-origin
    // iframe). Each write carries a STABLE idempotency key, generated ONCE and
    // mirrored BEFORE the first POST — which defeats the phantom-draft trap:
    // [client POSTs → SERVER PERSISTS → ack LOST (disconnect) → reconnect]. The item
    // is removed ONLY on a DEFINITIVE ack (2xx, or a terminal 4xx); a network/timeout
    // error KEEPS it queued. On reconnect the client re-sends the SAME key; the server
    // dedups the resend (idempotent 200) → the client clears the item — NO duplicate
    // row, NO phantom "unsaved". Truth = this queue's status, never a separate flag.
    // Boundary: dedup relies on the server's idempotency WINDOW.
    var QKEY = 'pd_dc_queue_v1', pending = [];
    // localStorage MIRROR DISABLED for now (owner 2026-07-25): captures go DIRECTLY to the server
    // via the in-memory queue below (POST + retry + idempotency all intact — a transient blip on the
    // submit is still handled in-session). We only drop the localStorage PERSISTENCE (survive-across-
    // reload / offline) so there is NO capture state in localStorage that can go stale, pollute a
    // sibling page, or resurrect a partial. Durable offline returns with the server-revision
    // architecture. Flip LS_DISABLED to false to restore the mirror.
    var LS_DISABLED = true;
    try {
      var sv = LS_DISABLED ? [] : JSON.parse((global.localStorage && global.localStorage.getItem(QKEY)) || '[]');
      if (Array.isArray(sv)) pending = sv.filter(validItem).slice(0, MAX_QUEUE); // validate + cap recovered queue
    } catch (e) {}
    function validItem(it) { return it && typeof it === 'object' && typeof it.writeKey === 'string' && typeof it.idempotencyKey === 'string'; }
    function mirror() {
      if (LS_DISABLED) return; // mirror off — in-memory queue only (send still resilient)
      try {
        if (!global.localStorage) return;
        // Mirror only serializable items — one poison item can't drop the whole queue.
        var safe = []; for (var i = 0; i < pending.length; i++) { try { var s = strip(pending[i]); JSON.stringify(s); safe.push(s); } catch (e) {} }
        global.localStorage.setItem(QKEY, JSON.stringify(safe));
      } catch (e) {}
    }
    function strip(it) { return { id: it.id, writeKey: it.writeKey, data: it.data, idempotencyKey: it.idempotencyKey, attempts: it.attempts }; }
    var statusMap = Object.create(null), statusCount = 0;
    function setStatus(id, s) {
      if (!(id in statusMap)) statusCount++;
      statusMap[id] = s;
      if (statusCount > MAX_STATUS) { for (var k in statusMap) if (statusMap[k] === 'saved' || statusMap[k] === 'error') { delete statusMap[k]; statusCount--; } } // evict terminal
      if (typeof opts.onStatus === 'function') { try { opts.onStatus(id, s); } catch (e) {} }
    }

    // save(descriptor, data): durably write a DATA OBJECT via a write CAPABILITY key.
    // GENERIC — the client knows nothing about what the write IS (a form, a quiz, an
    // upsert…). `descriptor.writeKey` is an opaque handle (symmetric to a read's
    // readKey); the server resolves it to the target + operation. Only { data,
    // idempotencyKey } goes on the wire — no page id, no form id — plus, ONLY on a page opened
    // from a class link, the student's `respondent` token (see CLASS-LINK RESPONDENT above).
    function save(descriptor, data) {
      var writeKey = descriptor && descriptor.writeKey;
      if (!writeKey) return Promise.reject(DataError('config', 'save needs a writeKey', { retryable: false }));
      if (destroyed) return Promise.reject(DataError('destroyed', 'client destroyed', { retryable: false }));
      var idem = genKey();
      try { JSON.stringify(data); } catch (e) { return Promise.reject(DataError('serialize', 'payload is not JSON-serializable', { retryable: false, cause: e })); } // reject up-front, never enqueue poison
      // NO client-side sandbox: preview and published use the IDENTICAL durable write
      // path (one code path — kills the "works in preview, not published" divergence).
      // TEST vs REAL is decided SERVER-SIDE from WHO submitted: a write authenticated as
      // an EDITOR of the form's folder (the author testing in-editor) is stamped
      // isTest:true and excluded from real dashboards; anonymous published visitors and
      // viewer-role members (students taking the quiz) are REAL. `mode` no longer gates
      // writes. See dataset write path + spec-pd-reactive-runtime.
      var item = { id: idem, writeKey: writeKey, data: data, idempotencyKey: idem, attempts: 0 };
      var p = new Promise(function (resolve, reject) { item._resolve = resolve; item._reject = reject; });
      pending.push(item); mirror(); setStatus(idem, 'saving'); drain();
      return p;
    }

    var draining = false, retryTimer = null, backoff = BASE_DELAY;
    function scheduleDrain() { if (retryTimer || destroyed) return; retryTimer = setTimeout(function () { retryTimer = null; backoff = Math.min(backoff * 2, MAX_DELAY); drain(); }, backoff + Math.random() * 400); }
    function headItem() { for (var i = 0; i < pending.length; i++) if (!pending[i]._parked) return pending[i]; return null; } // skip parked (exhausted) items — never head-of-line-block
    function settleItem(item, ok, err) { if (ok && item._resolve) { item._resolve(ok); } else if (!ok && item._reject) { item._reject(err); } item._resolve = item._reject = null; }
    function drain() {
      if (draining || destroyed) return;
      if (global.navigator && global.navigator.onLine === false) return; // paused offline; onOnline re-drains
      var item = headItem();
      if (!item) return;
      if (item.attempts >= MAX_WRITE) { // auto-retry exhausted → PARK (don't drop, don't block the queue); resumes on `online`
        item._parked = true; mirror(); setStatus(item.id, 'error');
        settleItem(item, null, DataError('exhausted', 'save failed after ' + MAX_WRITE + ' attempts; kept for retry on reconnect', { retryable: true }));
        return drain(); // continue with the next non-parked item
      }
      draining = true; item.attempts++; setStatus(item.id, 'saving');
      var body;
      try { var _payload = { data: item.data, idempotencyKey: item.idempotencyKey }; if (RESPONDENT) _payload.respondent = RESPONDENT; if (DEVICE) _payload.device = DEVICE; body = JSON.stringify(_payload); }
      catch (e) { draining = false; removeItem(item); mirror(); setStatus(item.id, 'error'); settleItem(item, null, DataError('serialize', 'payload not serializable', { retryable: false })); return drain(); }
      fetchOnce({ url: baseUrl + '/api/v1/ds/' + encodeURIComponent(item.writeKey), method: 'POST', credentialed: true }, WRITE_TO, body, item.idempotencyKey)
        .then(function (res) {
          draining = false; if (destroyed) return;
          if (res.ok) { removeItem(item); mirror(); backoff = BASE_DELAY; setStatus(item.id, 'saved'); settleItem(item, { id: item.id }); drain(); }
          else if (res.status >= 400 && res.status < 500 && !RETRY_STATUS[res.status]) { removeItem(item); mirror(); backoff = BASE_DELAY; setStatus(item.id, 'error');
            // Surface the server's REASON ({error:"…"}) — e.g. a validation rejection — so
            // the runtime can show WHY, not just "error". Best-effort body read; falls back
            // to the status code. (Never throws — a body-read failure still settles + drains.)
            res.text().then(function (t) { var msg = ''; try { var j = JSON.parse(t); msg = (j && j.error) || ''; } catch (e) {} return msg; }, function () { return ''; })
              .then(function (msg) { settleItem(item, null, DataError('http', msg ? String(msg).slice(0, 200) : ('HTTP ' + res.status), { status: res.status, retryable: false })); drain(); }); }
          else { setStatus(item.id, 'retrying'); scheduleDrain(); } // 5xx/429 → backoff retry
        }, function () { // network/timeout: server MAY have persisted it (lost ack) — KEEP the item; the idempotent re-send reconciles (no dup, no phantom draft)
          draining = false; if (destroyed) return; setStatus(item.id, 'retrying'); scheduleDrain();
        });
    }
    function removeItem(item) { var i = pending.indexOf(item); if (i >= 0) pending.splice(i, 1); }

    // ── connectivity: pause offline, re-drain on reconnect; revive parked items ──
    function onOnline() { if (destroyed) return; backoff = BASE_DELAY; pending.forEach(function (it) { if (it._parked) { it._parked = false; it.attempts = 0; } }); drain(); }
    if (global.addEventListener) global.addEventListener('online', onOnline);
    if (pending.length) drain(); // recover a prior session's mirrored writes

    function destroy() {
      destroyed = true;
      try { if (global.removeEventListener) global.removeEventListener('online', onOnline); } catch (e) {}
      if (retryTimer) { try { clearTimeout(retryTimer); } catch (e) {} retryTimer = null; }
      liveACs.slice().forEach(function (ac) { try { ac.abort(); } catch (e) {} }); liveACs.length = 0; // abort in-flight
      pending.forEach(function (it) { settleItem(it, null, DataError('destroyed', 'client destroyed', { retryable: false })); }); // never hang awaiters
      inflight = Object.create(null);
    }

    return {
      version: VERSION, mode: mode,
      read: read, save: save,
      drain: drain, status: statusMap, pendingCount: function () { return pending.length; },
      destroy: destroy, _resolveRead: resolveRead
    };
  }

  global.PDDataClient = { version: VERSION, boot: boot, respondentName: function () { return RESPONDENT_NAME; } };
})(typeof window !== 'undefined' ? window : globalThis);
